// 현재 문서의 CSS 를 새 창·파일로 옮기기 위한 직렬화

export type DocumentCss = {
  /** CSSOM 직렬화 규칙 전체. `<style>` 안에 그대로 넣을 것 */
  css: string;
  /** 규칙을 못 읽어 태그째 복제한 `<link rel="stylesheet">` outerHTML. `<style>` 앞에 붙이고 로드 대기 필요 */
  links: string[];
};

const readRules = (sheet: CSSStyleSheet): string | null => {
  try {
    return Array.from(sheet.cssRules)
      .map((rule) => rule.cssText)
      .join('\n');
  } catch {
    // 교차 출처 시트는 SecurityError
    return null;
  }
};

/**
 * 문서에 실린 CSS 전부를 직렬화.
 *
 * `<style>` 의 textContent 만 긁으면 프로덕션에서 스타일이 통째로 빔.
 * emotion 은 speedy 모드에서 규칙을 insertRule 로만 넣어 textContent 가 '' 이고,
 * Next 는 CSS 를 `<link>` 로 빼 `<style>` 셀렉터에 안 걸림. dev 는 둘 다 아니라 재현 안 됨.
 * 그래서 CSSOM(cssRules) 을 먼저 읽고, 못 읽는 시트만 폴백 — `<style>` 은 textContent,
 * `<link>` 는 태그 복제(sheet 가 없어 styleSheets 에 안 잡힌 링크 포함).
 */
export function collectDocumentCss(): DocumentCss {
  const chunks: string[] = [];
  const links: string[] = [];
  const collected = new Set<Node>();

  Array.from(document.styleSheets).forEach((sheet) => {
    const owner = sheet.ownerNode;
    if (owner) collected.add(owner);

    let text = readRules(sheet);
    if (text === null && owner instanceof HTMLLinkElement) {
      links.push(owner.outerHTML);
      return;
    }
    if (!text && owner) text = owner.textContent ?? '';
    if (!text) return;

    // media 조건은 cssRules 에 없어 그대로 풀면 화면용으로 승격됨
    const media = sheet.media?.mediaText;
    chunks.push(
      media && media !== 'all' ? `@media ${media} {\n${text}\n}` : text,
    );
  });

  // styleSheets 에 잡히지 않는 <style>(disabled 등) 보완
  document.querySelectorAll('style').forEach((el) => {
    if (collected.has(el)) return;
    const text = el.textContent ?? '';
    if (text) chunks.push(text);
  });
  document
    .querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')
    .forEach((el) => {
      if (!collected.has(el)) links.push(el.outerHTML);
    });

  return {
    // `content: "</style>"` 값이 <style> 블록을 조기 종료시키지 않게
    css: chunks.join('\n').replace(/<\/style/gi, '<\\/style'),
    links,
  };
}

const escapeAttr = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

/**
 * `<body>` 의 클래스와 `--*` 커스텀 프로퍼티만 `<body …>` 속성 문자열로.
 *
 * next/font 변수가 body 클래스·인라인 style 에 실려 있고 GlobalStyles 의
 * `font-family: var(--font-Pretendard), …` 가 읽음. 변수가 없으면 선언 전체가 무효.
 * 인라인 style 을 통째로 옮기지 않는 이유: MUI Modal 이 body 에 건 `overflow: hidden`
 * 스크롤 잠금까지 따라가 새 문서가 스크롤되지 않음.
 */
export function bodyAttrs(): string {
  const cls = document.body.className.trim();
  const decl = document.body.style;
  const style = Array.from(decl)
    .filter((name) => name.startsWith('--'))
    .map((name) => `${name}: ${decl.getPropertyValue(name).trim()}`)
    .join('; ');
  return (
    (cls ? ` class="${escapeAttr(cls)}"` : '') +
    (style ? ` style="${escapeAttr(style)}"` : '')
  );
}
